import { useState } from "react";
import { ArrowDown, Flame } from "lucide-react";
import { cn } from "@/lib/utils";
import { SlotPricing } from "@/hooks/useDynamicPricing";

type SortMode = "time" | "discount";

interface DynamicTimeSlotsProps {
  slots: SlotPricing[];
  selectedTime: string | undefined;
  onSelectTime: (time: string) => void;
}

const DynamicTimeSlots = ({ slots, selectedTime, onSelectTime }: DynamicTimeSlotsProps) => {
  const isFull = (slot: SlotPricing) => slot.spots === 0;

  return (
    <div className="space-y-3">
      {/* Time slots list */}
      <div className="space-y-2">
        {slots.map((slot) => {
          const isSelected = selectedTime === slot.time;
          const slotFull = isFull(slot);
          // A slot inside the online-booking cut-off is unavailable too.
          const isPast = slot.isPast || slot.isClosed;
          // B-109: a FULL slot stays selectable so the customer can pick it and
          // join its waitlist (the booking panel swaps "Book" for "Join the
          // waitlist" when a full slot is selected). Only PAST slots are truly
          // disabled. You can't waitlist a time that's already gone.
          const isDisabled = isPast;

          return (
            <button
              key={slot.time}
              type="button"
              disabled={isDisabled}
              onClick={() => onSelectTime(slot.time)}
              className={cn(
                "w-full flex items-center justify-between px-4 py-2.5 rounded-xl border transition-all text-left",
                isPast
                  ? "border-muted bg-muted/30 opacity-60 cursor-not-allowed"
                  : slotFull
                    ? "border-input bg-muted/20 hover:border-destructive/40"
                    : "border-input bg-background hover:border-muted-foreground/40",
                isSelected && "ring-2 ring-primary border-primary"
              )}
            >
              <div className="flex items-center gap-3">
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{slot.time}</span>
                  {slot.isCurrentBookingSlot ? (
                    <span className="text-[11px] text-primary font-medium">Your current time</span>
                  ) : slot.spots !== undefined && (
                    <span className={cn("text-[11px]", slotFull ? "text-destructive font-medium" : "text-muted-foreground", isPast && "text-muted-foreground")}>
                      {slot.isPast ? "Passed" : slot.isClosed ? "Booking closed" : slotFull ? "Fully booked" : `${slot.spots} spots left`}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right flex flex-col items-end">
                {slot.ruleName ? (
                  <>
                    <span className="text-xs text-muted-foreground line-through">
                      ${slot.originalPrice.toFixed(2)}
                    </span>
                    <span className="text-sm font-semibold text-green-600">
                      ${slot.adjustedPrice.toFixed(2)}
                    </span>
                    <span className="text-[10px] mt-0.5 text-green-600">
                      {slot.discountPercent}% off
                    </span>
                  </>
                ) : (
                  <span className="text-sm font-medium text-foreground">
                    ${slot.originalPrice.toFixed(2)}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DynamicTimeSlots;
